import React, { useState, useEffect, useRef } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Button,
  CircularProgress,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Box,
  Paper,
  Alert,
  Autocomplete,
  TextField,
  Tabs,
  Tab,
  Chip,
} from '@mui/material';
import AutoRenewIcon from '@mui/icons-material/Autorenew';
import EditIcon from '@mui/icons-material/Edit';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import UploadFileIcon from '@mui/icons-material/UploadFile';

import { useNavigate } from 'react-router-dom';
import {
  parseSwissPerfectTournament,
  recognizePlayer,
  quickRegisterPlayer,
  importSwissPerfectTournament,
} from '../../../services/parsing.service';
import { searchPlayers } from '../../../services/tournament.service';
import QuickRegisterDialog from './QuickRegisterDialog';

// ---------------------------------------------------------------------------
// Subcomponent: File Picker
// ---------------------------------------------------------------------------

function FilePicker({ label, accept, file, onChange, disabled }) {
  const inputRef = useRef(null);

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        p: 1.5,
        border: '1px solid',
        borderColor: file ? 'rgba(34, 197, 94, 0.4)' : 'rgba(255, 255, 255, 0.12)',
        borderRadius: 0,
        background: file ? 'rgba(34, 197, 94, 0.04)' : 'rgba(30, 41, 59, 0.4)',
        transition: 'all 0.2s',
        cursor: disabled ? 'default' : 'pointer',
        flex: 1,
      }}
      onClick={() => !disabled && inputRef.current?.click()}
    >
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        style={{ display: 'none' }}
        onChange={(e) => onChange(e.target.files?.[0] ?? null)}
        disabled={disabled}
      />
      <UploadFileIcon
        fontSize="small"
        sx={{ color: file ? '#22c55e' : '#64748b', flexShrink: 0 }}
      />
      <Box sx={{ minWidth: 0 }}>
        <Typography variant="caption" sx={{ color: '#64748b', display: 'block', textTransform: 'uppercase', letterSpacing: 0.5, fontSize: '0.65rem' }}>
          {label}
        </Typography>
        {file ? (
          <Typography variant="body2" sx={{ color: '#22c55e', fontWeight: 500, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {file.name}
          </Typography>
        ) : (
          <Typography variant="body2" sx={{ color: '#475569', fontStyle: 'italic' }}>
            Sin archivo seleccionado
          </Typography>
        )}
      </Box>
    </Box>
  );
}

// ---------------------------------------------------------------------------
// Main dialog
// ---------------------------------------------------------------------------

export default function SwissperfectImportDialog({ open, onClose }) {
  const navigate = useNavigate();

  // Files
  const [iniFile, setIniFile] = useState(null);
  const [trnFile, setTrnFile] = useState(null);
  const [scoFile, setScoFile] = useState(null);

  // Tournament data
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [tournament, setTournament] = useState(null);
  const [players, setPlayers] = useState([]);

  // Player recognition
  const [recognizing, setRecognizing] = useState(false);
  const [recognizingIndex, setRecognizingIndex] = useState(0);

  // Manual association dialog
  const [searchOpen, setSearchOpen] = useState(false);
  const [activePlayerIndex, setActivePlayerIndex] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);

  // Quick registration
  const [quickRegOpen, setQuickRegOpen] = useState(false);
  const [quickRegLoading, setQuickRegLoading] = useState(false);

  // Tabs
  const [activeTab, setActiveTab] = useState(0); // 0 = Jugadores, 1 = Partidas

  // ---------------------------------------------------------------------------
  // Handlers
  // ---------------------------------------------------------------------------

  const handleClose = () => {
    setIniFile(null);
    setTrnFile(null);
    setScoFile(null);
    setError(null);
    setTournament(null);
    setPlayers([]);
    setRecognizing(false);
    setRecognizingIndex(0);
    setActiveTab(0);
    onClose();
  };

  const handleLoadTournament = async () => {
    if (!iniFile || !trnFile || !scoFile) {
      setError('Por favor seleccioná los 3 archivos: .ini, .trn y .sco');
      return;
    }

    setError(null);
    setLoading(true);
    setTournament(null);
    setPlayers([]);

    try {
      const data = await parseSwissPerfectTournament(iniFile, trnFile, scoFile);
      setTournament(data);
      setActiveTab(0);
      if (data?.players) {
        const initialPlayers = data.players.map((p) => ({
          order: p.order,
          fullName: p.fullName,
          document: p.document,
          dbPlayer: null,
          status: 'idle', // 'idle' | 'loading' | 'success' | 'failed'
        }));
        setPlayers(initialPlayers);
      }
    } catch (err) {
      console.error(err);
      setError(err.message || 'Error al conectar con el servidor.');
    } finally {
      setLoading(false);
    }
  };

  const handleRecognizePlayers = async () => {
    if (players.length === 0 || recognizing) return;
    setRecognizing(true);

    for (let i = 0; i < players.length; i++) {
      setRecognizingIndex(i + 1);
      setPlayers((prev) => {
        const updated = [...prev];
        updated[i] = { ...updated[i], status: 'loading' };
        return updated;
      });

      try {
        const matchedPlayer = await recognizePlayer(players[i].fullName);
        setPlayers((prev) => {
          const updated = [...prev];
          if (matchedPlayer) {
            updated[i] = {
              ...updated[i],
              dbPlayer: {
                jug_id: matchedPlayer.jug_id,
                apellido_nombre: `${matchedPlayer.APELLIDO}, ${matchedPlayer.NOMBRE}`,
                elo: matchedPlayer.ELO,
              },
              status: 'success',
            };
          } else {
            updated[i] = { ...updated[i], dbPlayer: null, status: 'failed' };
          }
          return updated;
        });
      } catch (err) {
        console.error('Error recognizing player:', players[i].fullName, err);
        setPlayers((prev) => {
          const updated = [...prev];
          updated[i] = { ...updated[i], status: 'failed' };
          return updated;
        });
      }

      await new Promise((r) => setTimeout(r, 100));
    }

    setRecognizing(false);
  };

  const handleOpenSearch = (index) => {
    setActivePlayerIndex(index);
    setSearchQuery('');
    setSearchResults([]);
    setSearchOpen(true);
  };

  useEffect(() => {
    if (searchQuery.trim().length < 2) {
      setSearchResults([]);
      return;
    }
    const delayDebounce = setTimeout(async () => {
      setSearchLoading(true);
      try {
        const results = await searchPlayers(searchQuery);
        setSearchResults(results || []);
      } catch (err) {
        console.error(err);
      } finally {
        setSearchLoading(false);
      }
    }, 300);

    return () => clearTimeout(delayDebounce);
  }, [searchQuery]);

  const handleSelectDbPlayer = (player) => {
    if (activePlayerIndex === null) return;
    setPlayers((prev) => {
      const updated = [...prev];
      updated[activePlayerIndex] = {
        ...updated[activePlayerIndex],
        dbPlayer: {
          jug_id: player.jug_id,
          apellido_nombre: player.apellido_nombre,
          elo: player.elo,
        },
        status: 'success',
      };
      return updated;
    });
    setSearchOpen(false);
    setActivePlayerIndex(null);
  };

  const handleQuickRegisterSubmit = async (newPlayerData, resetForm) => {
    setQuickRegLoading(true);
    try {
      const result = await quickRegisterPlayer(newPlayerData);
      if (result?.jug_id) {
        handleSelectDbPlayer({
          jug_id: result.jug_id,
          apellido_nombre: result.apellido_nombre,
          elo: result.elo ?? 0,
        });
        setQuickRegOpen(false);
        resetForm();
      } else {
        throw new Error('No se pudo registrar el jugador.');
      }
    } catch (err) {
      console.error(err);
      alert(err.message || 'Error al registrar al jugador.');
    } finally {
      setQuickRegLoading(false);
    }
  };

  const handleConfirmImport = async () => {
    const unassociated = players.filter((p) => !p.dbPlayer?.jug_id);
    if (unassociated.length > 0) {
      setError('Todos los jugadores deben tener un jugador asignado de la base de datos.');
      return;
    }

    setError(null);
    setLoading(true);

    try {
      const playerMappings = players.map((p) => ({
        order: p.order,
        dbPlayerId: p.dbPlayer.jug_id,
      }));

      const importData = {
        name: tournament.name,
        system: tournament.system || 1,
        rounds: tournament.rounds,
        playerMappings,
      };

      const result = await importSwissPerfectTournament(importData);
      if (result?.torId) {
        handleClose();
        navigate(`/tournaments/edit/${result.torId}`);
      } else {
        throw new Error('No se recibió el ID del torneo importado.');
      }
    } catch (err) {
      console.error('Error al confirmar importación:', err);
      setError(err.message || 'Error al importar el torneo.');
    } finally {
      setLoading(false);
    }
  };

  // ---------------------------------------------------------------------------
  // Render helpers
  // ---------------------------------------------------------------------------

  const systemLabel = (system) => {
    if (system === 1) return 'Suizo';
    if (system === 2) return 'Round-robin';
    return 'Desconocido';
  };

  const cellSx = { background: '#1e293b', color: '#94a3b8', fontWeight: 'bold', borderColor: 'rgba(255, 255, 255, 0.08)' };

  // ---------------------------------------------------------------------------
  // JSX
  // ---------------------------------------------------------------------------

  return (
    <>
      <Dialog
        open={open}
        onClose={handleClose}
        fullWidth
        maxWidth="lg"
        PaperProps={{
          sx: {
            borderRadius: 0,
            background: '#0f172a',
            backgroundImage: 'none',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            color: '#cbd5e1',
          },
        }}
      >
        <DialogTitle sx={{ borderBottom: '1px solid rgba(255, 255, 255, 0.08)', pb: 2, fontWeight: 600 }}>
          Importar desde Swiss Perfect
        </DialogTitle>

        <DialogContent sx={{ mt: 3, display: 'flex', flexDirection: 'column', gap: 3 }}>

          {/* File pickers */}
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            <Typography variant="body2" sx={{ color: '#94a3b8', fontWeight: 500 }}>
              Seleccioná los 3 archivos exportados por Swiss Perfect:
            </Typography>
            <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
              <FilePicker
                label=".ini — Metadatos del torneo"
                accept=".ini"
                file={iniFile}
                onChange={setIniFile}
                disabled={loading || recognizing}
              />
              <FilePicker
                label=".trn — Jugadores (DBF)"
                accept=".trn"
                file={trnFile}
                onChange={setTrnFile}
                disabled={loading || recognizing}
              />
              <FilePicker
                label=".sco — Resultados (DBF)"
                accept=".sco"
                file={scoFile}
                onChange={setScoFile}
                disabled={loading || recognizing}
              />
            </Box>
            <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
              <Button
                variant="contained"
                onClick={handleLoadTournament}
                disabled={loading || recognizing || !iniFile || !trnFile || !scoFile}
                sx={{
                  background: '#3a6eb6',
                  borderRadius: 0,
                  height: '40px',
                  px: 4,
                  '&:hover': { background: '#2a5793' },
                }}
              >
                {loading ? <CircularProgress size={22} color="inherit" /> : 'Cargar torneo'}
              </Button>
            </Box>
          </Box>

          {error && <Alert severity="error" sx={{ borderRadius: 0 }}>{error}</Alert>}

          {/* Tournament metadata + players */}
          {tournament && (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>

              {/* Metadata card */}
              <Paper
                variant="outlined"
                sx={{
                  p: 2,
                  background: 'rgba(30, 41, 59, 0.5)',
                  borderColor: 'rgba(255, 255, 255, 0.08)',
                  borderRadius: 0,
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  flexWrap: 'wrap',
                  gap: 2,
                }}
              >
                <Box>
                  <Typography variant="caption" sx={{ color: '#64748b', textTransform: 'uppercase', letterSpacing: 1 }}>
                    Torneo Detectado
                  </Typography>
                  <Typography variant="h6" sx={{ color: '#b1b2b3', fontWeight: 600 }}>
                    {tournament.name}
                  </Typography>
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mt: 0.5 }}>
                    <Chip
                      label={`Sistema: ${systemLabel(tournament.system)}`}
                      size="small"
                      sx={{ background: 'rgba(58, 110, 182, 0.2)', color: '#94a3b8', borderRadius: 0, height: 20, fontSize: '0.7rem' }}
                    />
                    <Chip
                      label={`Rondas: ${tournament.rounds?.length ?? 0}`}
                      size="small"
                      sx={{ background: 'rgba(58, 110, 182, 0.2)', color: '#94a3b8', borderRadius: 0, height: 20, fontSize: '0.7rem' }}
                    />
                    <Chip
                      label={`Jugadores: ${players.length}`}
                      size="small"
                      sx={{ background: 'rgba(58, 110, 182, 0.2)', color: '#94a3b8', borderRadius: 0, height: 20, fontSize: '0.7rem' }}
                    />
                    {tournament.arbiter && (
                      <Chip
                        label={`Árbitro: ${tournament.arbiter}`}
                        size="small"
                        sx={{ background: 'rgba(30, 41, 59, 0.8)', color: '#64748b', borderRadius: 0, height: 20, fontSize: '0.7rem' }}
                      />
                    )}
                  </Box>
                </Box>
                <Button
                  variant="contained"
                  color="secondary"
                  startIcon={<AutoRenewIcon />}
                  onClick={handleRecognizePlayers}
                  disabled={recognizing}
                  sx={{
                    background: '#1c5b77',
                    borderRadius: 0,
                    '&:hover': { background: '#356075ff' },
                  }}
                >
                  {recognizing
                    ? `Reconociendo ${recognizingIndex}/${players.length}...`
                    : 'Reconocer Jugadores'}
                </Button>
              </Paper>

              {/* Tabs */}
              <Box sx={{ borderBottom: 1, borderColor: 'rgba(255, 255, 255, 0.08)' }}>
                <Tabs
                  value={activeTab}
                  onChange={(e, val) => setActiveTab(val)}
                  textColor="inherit"
                  indicatorColor="primary"
                  sx={{
                    '& .MuiTabs-indicator': { backgroundColor: '#3a6eb6' },
                    '& .MuiTab-root': {
                      color: '#94a3b8',
                      textTransform: 'none',
                      fontWeight: 600,
                      '&.Mui-selected': { color: '#cbd5e1' },
                    },
                  }}
                >
                  <Tab label="Jugadores" />
                  <Tab label="Partidas detectadas" />
                </Tabs>
              </Box>

              {/* Tab: Jugadores */}
              {activeTab === 0 && (
                <TableContainer sx={{ border: '1px solid rgba(255, 255, 255, 0.08)', maxHeight: '400px' }}>
                  <Table size="small" stickyHeader>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={cellSx}>#</TableCell>
                        <TableCell sx={cellSx}>Nombre (Swiss Perfect)</TableCell>
                        <TableCell sx={cellSx}>Documento</TableCell>
                        <TableCell sx={cellSx}>Jugador (Base de Datos)</TableCell>
                        <TableCell sx={cellSx}>ID</TableCell>
                        <TableCell sx={cellSx}>ELO</TableCell>
                        <TableCell align="right" sx={cellSx}>Acciones</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {players.map((p, idx) => (
                        <TableRow key={p.order ?? idx} sx={{ '&:hover': { background: '#2a334d !important' } }}>
                          <TableCell sx={{ color: '#94a3b8', py: 1, borderColor: 'rgba(255, 255, 255, 0.05)' }}>
                            {p.order}
                          </TableCell>
                          <TableCell sx={{ color: '#cbd5e1', py: 1, fontWeight: 500, borderColor: 'rgba(255, 255, 255, 0.05)' }}>
                            {p.fullName}
                          </TableCell>
                          <TableCell sx={{ color: '#64748b', py: 1, borderColor: 'rgba(255, 255, 255, 0.05)' }}>
                            {p.document || '—'}
                          </TableCell>
                          <TableCell sx={{ py: 1, borderColor: 'rgba(255, 255, 255, 0.05)' }}>
                            {p.status === 'loading' ? (
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <CircularProgress size={16} />
                                <Typography variant="body2" sx={{ color: '#94a3b8' }}>Reconociendo...</Typography>
                              </Box>
                            ) : p.status === 'success' && p.dbPlayer ? (
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: '#22c55e' }}>
                                <CheckCircleIcon fontSize="small" />
                                <Typography variant="body2" sx={{ fontWeight: 500 }}>{p.dbPlayer.apellido_nombre}</Typography>
                              </Box>
                            ) : p.status === 'failed' ? (
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, color: '#ef4444' }}>
                                <CancelIcon fontSize="small" />
                                <Typography variant="body2">No se pudo reconocer al jugador</Typography>
                              </Box>
                            ) : (
                              <Typography variant="body2" sx={{ color: '#64748b', fontStyle: 'italic' }}>Sin verificar</Typography>
                            )}
                          </TableCell>
                          <TableCell sx={{ color: '#94a3b8', py: 1, borderColor: 'rgba(255, 255, 255, 0.05)' }}>
                            {p.dbPlayer?.jug_id || '—'}
                          </TableCell>
                          <TableCell sx={{ color: '#94a3b8', py: 1, borderColor: 'rgba(255, 255, 255, 0.05)' }}>
                            {p.dbPlayer?.elo ?? '—'}
                          </TableCell>
                          <TableCell align="right" sx={{ py: 1, borderColor: 'rgba(255, 255, 255, 0.05)' }}>
                            <Button
                              size="small"
                              variant="outlined"
                              startIcon={<EditIcon />}
                              onClick={() => handleOpenSearch(idx)}
                              disabled={recognizing}
                              sx={{
                                color: '#3a6eb6',
                                borderColor: 'rgba(58, 110, 182, 0.25)',
                                borderRadius: 0,
                                py: 0.25,
                                '&:hover': {
                                  borderColor: '#3a6eb6',
                                  background: 'rgba(58, 110, 182, 0.05)',
                                },
                              }}
                            >
                              Cambiar
                            </Button>
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              )}

              {/* Tab: Partidas */}
              {activeTab === 1 && (
                <Box
                  sx={{
                    p: 4,
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    minHeight: '250px',
                    border: '1px dashed rgba(255, 255, 255, 0.1)',
                    background: 'rgba(30, 41, 59, 0.2)',
                  }}
                >
                  <Typography variant="body1" sx={{ color: '#cbd5e1', mb: 1, fontWeight: 600 }}>
                    Visualización de Partidas
                  </Typography>
                  <Typography variant="body2" sx={{ color: '#64748b', textAlign: 'center', maxWidth: '400px' }}>
                    Próximamente: Listado de partidas y emparejamientos leídos de Swiss Perfect a modo de validación visual e informativa antes de confirmar la importación.
                  </Typography>
                </Box>
              )}
            </Box>
          )}
        </DialogContent>

        <DialogActions sx={{ p: 3, borderTop: '1px solid rgba(255, 255, 255, 0.08)', gap: 1.5 }}>
          <Button
            variant="outlined"
            onClick={handleClose}
            disabled={recognizing}
            sx={{
              color: '#94a3b8',
              borderColor: 'rgba(255, 255, 255, 0.15)',
              borderRadius: 0,
              '&:hover': {
                borderColor: '#cbd5e1',
                background: 'rgba(255, 255, 255, 0.05)',
              },
            }}
          >
            Cancelar
          </Button>
          <Button
            variant="contained"
            onClick={handleConfirmImport}
            disabled={loading || recognizing || !tournament}
            sx={{
              background: '#3a6eb6',
              borderRadius: 0,
              px: 4,
              '&:hover': { background: '#2a5793' },
            }}
          >
            {loading ? <CircularProgress size={24} color="inherit" /> : 'Confirmar Importación'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Manual Search Dialog */}
      <Dialog
        open={searchOpen}
        onClose={() => setSearchOpen(false)}
        PaperProps={{
          sx: {
            borderRadius: 0,
            background: '#1e293b',
            backgroundImage: 'none',
            border: '1px solid rgba(255, 255, 255, 0.12)',
            color: '#cbd5e1',
            width: '100%',
            maxWidth: '500px',
          },
        }}
      >
        <DialogTitle sx={{ borderBottom: '1px solid rgba(255, 255, 255, 0.08)', pb: 1.5, fontWeight: 600 }}>
          Asociar Jugador
        </DialogTitle>
        <DialogContent sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2.5 }}>
          {activePlayerIndex !== null && (
            <Typography variant="body2" sx={{ color: '#94a3b8' }}>
              Buscando reemplazo para: <strong>{players[activePlayerIndex]?.fullName}</strong>
            </Typography>
          )}

          <Autocomplete
            value={null}
            onChange={(e, value) => value && handleSelectDbPlayer(value)}
            inputValue={searchQuery}
            onInputChange={(e, value) => setSearchQuery(value)}
            options={searchResults}
            getOptionLabel={(option) => `${option.apellido_nombre} (DNI: ${option.documento})`}
            loading={searchLoading}
            noOptionsText={searchQuery.length < 2 ? 'Escribe al menos 2 letras...' : 'No se encontraron jugadores'}
            PaperComponent={({ children }) => (
              <Paper sx={{ borderRadius: 0, background: '#0f172a', color: '#cbd5e1', border: '1px solid rgba(255, 255, 255, 0.15)' }}>
                {children}
              </Paper>
            )}
            ListboxProps={{
              sx: {
                backgroundColor: '#0f172a',
                color: '#94a3b8',
                '& .MuiAutocomplete-option': {
                  borderRadius: 0,
                  '&[aria-selected="true"]': { backgroundColor: 'rgba(58, 110, 182, 0.25) !important', color: '#cbd5e1' },
                  '&[aria-selected="true"].Mui-focused': { backgroundColor: 'rgba(58, 110, 182, 0.3) !important' },
                  '&.Mui-focused': { backgroundColor: 'rgba(255, 255, 255, 0.08) !important' },
                  '&:hover': { backgroundColor: 'rgba(255, 255, 255, 0.05)' },
                },
              },
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                autoFocus
                label="Buscar Jugador en BD"
                placeholder="Ej: Pérez, Juan"
                InputLabelProps={{ style: { color: '#94a3b8' } }}
                inputProps={{ ...params.inputProps, style: { color: '#cbd5e1' } }}
                sx={{
                  '& .MuiOutlinedInput-root': {
                    borderRadius: 0,
                    '& fieldset': { borderColor: 'rgba(255, 255, 255, 0.15)', borderRadius: 0 },
                    '&:hover fieldset': { borderColor: '#3a6eb6' },
                    '&.Mui-focused fieldset': { borderColor: '#3a6eb6' },
                  },
                }}
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <React.Fragment>
                      {searchLoading ? <CircularProgress color="inherit" size={20} /> : null}
                      {params.InputProps.endAdornment}
                    </React.Fragment>
                  ),
                }}
              />
            )}
          />

          <Box sx={{ borderTop: '1px solid rgba(255, 255, 255, 0.08)', pt: 2, display: 'flex', flexDirection: 'column', gap: 1.5 }}>
            <Typography variant="body2" sx={{ color: '#94a3b8' }}>
              ¿El jugador no existe en la base de datos? Regístralo de forma rápida:
            </Typography>
            <Button
              variant="outlined"
              color="primary"
              startIcon={<PersonAddIcon />}
              onClick={() => setQuickRegOpen(true)}
              sx={{
                color: '#3a6eb6',
                borderColor: 'rgba(58, 110, 182, 0.3)',
                borderRadius: 0,
                '&:hover': { borderColor: '#3a6eb6', background: 'rgba(58, 110, 182, 0.05)' },
              }}
            >
              Registrar nuevo jugador
            </Button>
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 3, borderTop: '1px solid rgba(255, 255, 255, 0.08)' }}>
          <Button
            variant="outlined"
            onClick={() => setSearchOpen(false)}
            sx={{
              color: '#94a3b8',
              borderColor: 'rgba(255, 255, 255, 0.15)',
              borderRadius: 0,
              '&:hover': { borderColor: '#cbd5e1', background: 'rgba(255, 255, 255, 0.05)' },
            }}
          >
            Cerrar
          </Button>
        </DialogActions>
      </Dialog>

      {/* Quick Registration Sub-Dialog */}
      <QuickRegisterDialog
        open={quickRegOpen}
        onClose={() => setQuickRegOpen(false)}
        onSubmit={handleQuickRegisterSubmit}
        loading={quickRegLoading}
      />
    </>
  );
}
